import { useMemo } from "react";

const UsageAnalytics = ({ chats = [] }) => {
  const analytics = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 30);

    let activeToday = 0;
    let privateChats = 0;
    let spectraChats = 0;
    let messagesThisWeek = 0;
    let messagesThisMonth = 0;
    
    // Last 7 days: individual daily bars
    const dailyActivity = new Array(7).fill(0);
    // Previous 23 days: grouped into 4 weekly bars (days 7-13, 14-20, 21-27, 28-30)
    const weeklyActivity = new Array(4).fill(0);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    chats.forEach(chat => {
      const updatedAt = chat.updated_at ? new Date(chat.updated_at) : null;
      const messageCount = chat.message_count || 0;
      
      // Count active today
      if (updatedAt && updatedAt >= today) {
        activeToday++;
      }

      // Count private vs spectra
      if (chat.space) {
        spectraChats++;
      } else {
        privateChats++;
      }

      // Count messages for week and month
      if (updatedAt && updatedAt >= weekAgo) {
        messagesThisWeek += messageCount;
      }
      if (updatedAt && updatedAt >= monthAgo) {
        messagesThisMonth += messageCount;
      }

      // Activity distribution over 30 days
      if (updatedAt && updatedAt >= monthAgo) {
        const daysAgo = Math.floor((now - updatedAt) / (1000 * 60 * 60 * 24));
        
        if (daysAgo < 7) {
          // Last 7 days: daily granularity
          dailyActivity[6 - daysAgo] += messageCount;
        } else if (daysAgo < 30) {
          // Days 7-29: weekly granularity
          const weekIndex = Math.floor((daysAgo - 7) / 7);
          if (weekIndex < 4) {
            weeklyActivity[weekIndex] += messageCount;
          }
        }
      }
    });

    // Combine weekly and daily for chart (weekly bars first, then daily)
    const chartData = [...[...weeklyActivity].reverse(), ...dailyActivity];
    const maxActivity = Math.max(...chartData, 1);
    const todayIndex = now.getDay();

    return {
      total: chats.length,
      activeToday,
      privateChats,
      spectraChats,
      messagesThisWeek,
      messagesThisMonth,
      chartData,
      maxActivity,
      todayIndex,
      days
    };
  }, [chats]);

  const getBarHeight = (value) => {
    if (analytics.maxActivity === 0) return 4;
    return Math.max(4, (value / analytics.maxActivity) * 100);
  };

  const getChartLabels = () => {
    const labels = [];
    const now = new Date();
    
    // 4 weekly labels for weeks 4-1 (oldest to newest)
    labels.push('W-4', 'W-3', 'W-2', 'W-1');
    
    // 7 daily labels for last 7 days
    const todayIndex = now.getDay();
    const startDay = (todayIndex - 6 + 7) % 7;
    for (let i = 0; i < 7; i++) {
      labels.push(analytics.days[(startDay + i) % 7]);
    }
    
    return labels;
  };

  const chartLabels = getChartLabels();

  return (
    <div className="bg-fill-white dark:bg-fill-dialog-surface rounded-2xl border border-edge-gray-200 dark:border-edge-panel-border p-6">
      <div className="flex items-center gap-2 mb-4">
        <svg className="w-5 h-5 text-ink-primary" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
        <h3 className="font-semibold text-ink-ink dark:text-ink-white">
          Usage Analytics - Last 30 Days
        </h3>
      </div>

      {/* Chart */}
      <div className="mb-6">
        <div className="flex items-end justify-between h-32 gap-2 px-4">
          {analytics.chartData.map((value, index) => {
            const isWeekly = index < 4;
            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex items-end justify-center" style={{ height: '100px' }}>
                  <div
                    className={`w-full rounded-t-lg transition-all duration-300 hover:opacity-80 relative group ${
                      isWeekly 
                        ? 'bg-gradient-to-t from-gradient-purple-600 to-gradient-hex-c084fc'
                        : 'bg-gradient-to-t from-gradient-gradient-primary to-gradient-accent'
                    }`}
                    style={{ height: `${getBarHeight(value)}%` }}
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-fill-ink dark:bg-fill-white text-ink-white dark:text-ink-ink px-2 py-1 rounded text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {value} msgs
                    </div>
                  </div>
                </div>
                <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400 font-medium">
                  {chartLabels[index]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-edge-gray-200 dark:border-edge-panel-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-fill-sidebar-surface dark:bg-fill-hex-1e3a5f flex items-center justify-center">
            <svg className="w-4 h-4 text-ink-primary" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
              <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
            </svg>
          </div>
          <div>
            <div className="text-lg font-bold text-ink-ink dark:text-ink-white">
              {analytics.total}
            </div>
            <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
              Total Chats
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-fill-hex-fff4ed dark:bg-fill-hex-5f2e1e flex items-center justify-center">
            <svg className="w-4 h-4 text-ink-hex-f97316" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <div className="text-lg font-bold text-ink-ink dark:text-ink-white">
              {analytics.activeToday}
            </div>
            <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
              Active Today
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-fill-green-50 dark:bg-fill-hex-1e3a2e flex items-center justify-center">
            <svg className="w-4 h-4 text-ink-emerald-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </div>
          <div>
            <div className="text-lg font-bold text-ink-ink dark:text-ink-white">
              {analytics.messagesThisWeek}
            </div>
            <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
              Msgs This Week
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-fill-hex-fef3c7 dark:bg-fill-hex-5f4e1e flex items-center justify-center">
            <svg className="w-4 h-4 text-ink-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <div className="text-lg font-bold text-ink-ink dark:text-ink-white">
              {analytics.messagesThisMonth}
            </div>
            <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
              Msgs This Month
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-fill-blue-50 dark:bg-fill-hex-1e3a5f flex items-center justify-center">
            <svg className="w-4 h-4 text-ink-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
            </svg>
          </div>
          <div>
            <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400 leading-tight">
              🔒 {analytics.privateChats} Private
            </div>
            <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400 leading-tight">
              🌐 {analytics.spectraChats} Spectra
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;
